import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useMSSP from "../../../../api/hooks/useMSSP";
import { RDS_ENTITIES_QUERY_KEY_PREFIX } from "../components/constants";
import { useEntities } from "./useEntities";

const DEFAULT_PAGE_SIZE = 100;
const SEARCH_QUERY_MIN_LEN = 3;

export default function useRdsEntitiesInfinite({
  entityType,
  sortModel,
  visibleColumns,
  filtersPayload,
  freetextQuery,
  enabled,
  pageSize = DEFAULT_PAGE_SIZE,
  queryKeyPrefix = RDS_ENTITIES_QUERY_KEY_PREFIX,
}) {
  const { getEntitiesRds } = useAPI();
  const { selectedTenantIds, rerenderKey } = useMSSP();
  const { flattenEntities } = useEntities();

  const tenantKey = selectedTenantIds?.join(",") || "";

  const primarySort =
    Array.isArray(sortModel) && sortModel.length > 0 ? sortModel[0] : null;
  const sort_by = primarySort?.field || null;
  const sort_dir = primarySort?.sort || null;

  const columns = useMemo(() => {
    if (
      !visibleColumns ||
      !Array.isArray(visibleColumns) ||
      visibleColumns.length === 0
    ) {
      return null;
    }
    return visibleColumns;
  }, [visibleColumns]);

  const normalizedFiltersPayload = useMemo(() => {
    if (!filtersPayload) return null;
    return {
      filters: Array.isArray(filtersPayload.filters)
        ? filtersPayload.filters
        : [],
      filter_logic: Array.isArray(filtersPayload.filter_logic)
        ? filtersPayload.filter_logic
        : [],
    };
  }, [filtersPayload]);

  const queryKey = useMemo(() => {
    return [
      queryKeyPrefix,
      entityType || "all",
      tenantKey,
      rerenderKey,
      sort_by,
      sort_dir,
      columns ? columns.join(",") : "no-columns",
      normalizedFiltersPayload,
      freetextQuery ? freetextQuery.trim() : "",
      pageSize,
    ];
  }, [
    queryKeyPrefix,
    entityType,
    tenantKey,
    rerenderKey,
    sort_by,
    sort_dir,
    columns,
    normalizedFiltersPayload,
    freetextQuery,
    pageSize,
  ]);

  return useInfiniteQuery({
    queryKey,
    enabled: Boolean(enabled && entityType),

    refetchOnWindowFocus: false,
    retry: 1,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const response = await getEntitiesRds({
        entity_type: entityType,
        page: pageParam,
        page_size: pageSize,
        sort_by,
        sort_dir,
        columns,
        filters: normalizedFiltersPayload?.filters || [],
        filter_logic: normalizedFiltersPayload?.filter_logic || [],
        query:
          freetextQuery && freetextQuery.trim().length >= SEARCH_QUERY_MIN_LEN
            ? freetextQuery.trim()
            : null,
        include_total: pageParam === 1,
      });
      const data = response.data || {};
      return {
        entities: flattenEntities(data.entities || []),
        pagination: data.pagination || {},
      };
    },
    getNextPageParam: (lastPage) => {
      const hasNext = Boolean(lastPage?.pagination?.has_next);
      if (!hasNext) return undefined;
      const page = lastPage?.pagination?.page || 1;
      return page + 1;
    },
    select: (data) => {
      const pages = data?.pages || [];
      const entities = pages.flatMap((p) => p.entities || []);
      const totalCount = pages?.[0]?.pagination?.total_count || 0;
      const hasNextPage = Boolean(
        pages?.[pages.length - 1]?.pagination?.has_next
      );
      return {
        pages,
        pageParams: data.pageParams,
        entities,
        totalCount,
        hasNextPage,
      };
    },
  });
}
